import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router';

import ToastViewport from '@/components/Toast';
import { applyBrandDefaultLanguage } from '@/i18n';
import router from '@/router';
import { BookingService, initLiffFromBrand, isLiffInClient } from '@/services';
import { useBrandStore, useBookingStore } from '@/store';
import { updateBrandTheme } from '@/utils/theme';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { brand, setBrand, setStoreId } = useBrandStore();
  const setBookingStoreId = useBookingStore((s) => s.setStoreId);
  const { i18n } = useTranslation();

  useEffect(() => {
    BookingService.ifDoneThenClearBookingState();

    // React Router navigations do not remount the app, so rerun the booking
    // cleanup check whenever the user leaves the Thank You page inside the SPA.
    const unsubscribe = router.subscribe(() => {
      BookingService.ifDoneThenClearBookingState();
    });

    return unsubscribe;
  }, []);

  // Refresh available treatments when language changes
  useEffect(() => {
    const { customers, setAvailableTreatments } = useBookingStore.getState();

    // Only refresh if there's at least one customer with at least one treatment item
    const hasCustomersWithTreatments = customers.some(
      (customer) =>
        customer.treatmentItems && customer.treatmentItems.length > 0,
    );

    if (hasCustomersWithTreatments) {
      const refreshTreatments = async () => {
        try {
          console.log(
            '🌐 Language changed - refreshing available treatments...',
          );
          const treatments = await BookingService.getTreatments(customers, 0);
          setAvailableTreatments(treatments);
          console.log('✅ Available treatments refreshed');
        } catch (error) {
          console.error(
            '❌ Failed to refresh treatments on language change:',
            error,
          );
        }
      };

      void refreshTreatments();
    }
  }, [i18n.language]);

  // Global brand info loading - runs once when the app starts
  useEffect(() => {
    const loadBrandInfo = async () => {
      try {
        console.log('🏢 Loading brand information...');
        setIsLoading(true);
        setHasError(false);

        const brandData = await BookingService.getBrandInfo();
        setBrand(brandData);

        // Only bootstrap LIFF inside the LINE in-app browser.
        if (brandData.liffId && isLiffInClient()) {
          await initLiffFromBrand(brandData.liffId);

          if (window.location.pathname === '/') {
            await router.navigate('/booking', { replace: true });
          }
        }

        // Apply brand colors to CSS custom properties for dynamic theming
        updateBrandTheme(brandData);

        // Move language selection responsibility into i18n module
        applyBrandDefaultLanguage(brandData.defaultLanguage);

        // Set HTML title
        document.title = `${brandData.name} | Booking`;

        // Set favicon
        const favicon = document.querySelector("link[rel*='icon']");
        if (favicon) {
          (favicon as HTMLLinkElement).href = brandData.logoImageUrl;
        } else {
          // Create favicon link if it doesn't exist
          const newFavicon = document.createElement('link');
          newFavicon.rel = 'icon';
          newFavicon.href = brandData.logoImageUrl;
          document.head.appendChild(newFavicon);
        }

        console.log('✅ Brand information loaded and theme applied');
        setIsLoading(false);
      } catch (error) {
        console.error('❌ Failed to load brand info:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    // Only load if we don't have brand data yet
    if (!brand) {
      loadBrandInfo();
    } else {
      setIsLoading(false);
    }

    // If a storeId query param exists, populate booking store
    try {
      const params = new URLSearchParams(window.location.search);
      const storeIdParam = params.get('storeId');
      if (storeIdParam) {
        const id = Number(storeIdParam);
        if (!Number.isNaN(id) && id > 0) {
          setBookingStoreId(id);
          setStoreId(id);
          BookingService.setStoreId(id);
        }
      }

      // If returning from LIFF login, navigate to the stored path
      // const returnPath = params.get('liff.state');
      // if (returnPath && returnPath !== '/') {
      //   // Use navigate after a brief delay to ensure router is ready
      //   setTimeout(() => {
      //     void router.navigate(returnPath);
      //   }, 0);
      // }
    } catch (err) {
      console.error('BookingService.setStoreId error', err);
    }
  }, [brand, setBrand, setBookingStoreId, setStoreId]);

  // Show loading screen while brand is loading
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          {/* Spinner */}
          <div className="border-t-primary h-12 w-12 animate-spin rounded-full border-4 border-gray-300"></div>
          {/* Loading text */}
          <div className="mt-4 text-sm font-medium text-gray-800">
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Show error state with reload button
  if (hasError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mb-4 text-lg text-gray-600">
            Failed to load application
          </div>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-gray-800 px-6 py-3 text-white hover:bg-gray-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // Render the main app once brand is loaded
  return (
    <>
      <RouterProvider router={router} />
      <ToastViewport />
    </>
  );
}

export default App;
